<!--
	@name app-account-settings-dialog-app-login
	@description Hand the current signed in session over to the Air mobile app via a QR code and short code
	@date 2026/08/12
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<common-dialog ref="dialog" :component="$options.name" :max-width="600" @closed="reset">
		<template #header>
			Log in to the Air app
		</template>
		<template #body>
			<v-container class="pa-0 text-center">
				<!-- Loading -->
				<div v-if="state === 'loading'" class="py-10">
					<v-progress-circular indeterminate color="primary" size="48" />
					<p class="text-body-2 mt-4 mb-0">
						Preparing your code...
					</p>
				</div>

				<!-- Failed to open a session -->
				<div v-else-if="state === 'error'" class="py-8">
					<v-icon color="error" size="48">
						mdi-alert-circle-outline
					</v-icon>
					<p class="text-body-1 mt-4">
						We could not create a login code. Please try again.
					</p>
					<v-btn color="primary" depressed :disabled="busy" :loading="busy" @click="load()">
						Try again
					</v-btn>
				</div>

				<!-- Device logged in -->
				<div v-else-if="state === 'success'" class="py-8">
					<v-icon color="success" size="56">
						mdi-check-circle-outline
					</v-icon>
					<p class="text-h6 mt-4 mb-1">
						You're signed in
					</p>
					<p class="text-body-2 mb-0">
						The Air app on your phone is now logged in to your account.
					</p>
				</div>

				<!-- Expired -->
				<div v-else-if="state === 'expired'" class="py-8">
					<v-icon color="warning" size="48">
						mdi-timer-off-outline
					</v-icon>
					<p class="text-body-1 mt-4">
						This code has expired.
					</p>
					<v-btn color="primary" depressed :disabled="cooldown > 0 || busy" :loading="busy" @click="load(true)">
						{{ cooldown > 0 ? `Generate in ${cooldown}s` : 'Generate a new code' }}
					</v-btn>
				</div>

				<!-- Ready to scan -->
				<div v-else>
					<!-- The device has opened the link. The scan is the step most likely to fail
					     silently, so say plainly when it has worked -->
					<v-alert v-if="connected" dense text type="success" class="text-body-2 text-left mb-5">
						Device connected. Enter the code below on your phone.
					</v-alert>
					<p v-else class="text-body-2 mb-5">
						Scan this code with your phone's camera, then enter the number below into the Air app to sign in.
					</p>

					<v-sheet class="d-inline-block pa-3 rounded" color="white">
						<v-img :src="session.qr" alt="QR code to log in to the Air app" width="200" height="200" />
					</v-sheet>

					<v-sheet class="blue-grey lighten-5 rounded mt-6 py-4 px-5">
						<p class="text-caption text-uppercase blue-grey--text mb-1">
							Your code
						</p>
						<div class="app-login-code blue-grey--text text--darken-3">
							{{ session.code }}
						</div>
						<p class="text-caption mb-0 mt-2">
							Expires in <strong>{{ countdown }}</strong>
						</p>
					</v-sheet>

					<p v-if="user.name" class="text-body-2 mt-5 mb-0">
						You'll be signed in as <strong>{{ user.name }}</strong>
					</p>

					<v-btn text small color="primary" class="mt-3" :disabled="cooldown > 0 || busy" :loading="busy" @click="load(true)">
						{{ cooldown > 0 ? `Generate in ${cooldown}s` : 'Generate a new code' }}
					</v-btn>

					<p class="text-caption blue-grey--text mt-4 mb-0">
						Never share this code with anyone. Air will never ask you for it.
					</p>
				</div>
			</v-container>
		</template>
	</common-dialog>
</template>

<script>
	import { mapActions, mapState } from 'vuex';
	import CommonDialog from '@/component/common/dialog';

	// Long enough to stop accidental double taps and impatient re-clicks without getting in the way.
	// The server enforces its own limit; this only keeps well behaved clients away from it.
	const REGENERATE_COOLDOWN = 10;

	export default {
		name: 'app-account-settings-dialog-app-login',

		components: { CommonDialog },

		data() {
			return {
				state: 'loading',
				session: {},
				connected: false,
				// Covers every request this dialog makes, not just regeneration, so no button that
				// fires one can be pressed twice while it is in flight
				busy: false,
				remaining: 0,
				cooldown: 0,
				interval: null,
				timeout: null
			};
		},

		computed: {
			...mapState('Account', ['user']),

			/**
			 * @name countdown
			 * @description Remaining lifetime of the current code as mm:ss
			 * @return {String}
			 */
			countdown() {
				const minutes = Math.floor(this.remaining / 60);
				const seconds = this.remaining % 60;
				return `${minutes}:${String(seconds).padStart(2, '0')}`;
			}
		},

		beforeDestroy() {
			this.reset();
		},

		methods: {
			...mapActions('AccountAppLogin', ['createAppLoginSession']),

			/**
			 * @name open
			 * @description Open the dialog and pick up the running code, or start a new one
			 */
			open() {
				this.$refs.dialog.open();
				this.load();
			},

			/**
			 * @name close
			 * @description Close the dialog
			 */
			close() {
				this.$refs.dialog.close();
			},

			/**
			 * @name load
			 * @description Fetch the session and start listening for the device to work through it.
			 * Without regenerate the backend hands back the code already running, so closing and
			 * reopening does not strand a half typed code on the phone
			 * @param {Boolean} regenerate Deliberately replace the running session
			 */
			async load(regenerate = false) {
				// Guards the request itself, not just the button. Disabling is bound to the same flag,
				// but the binding only takes effect on the next render, which a fast double tap beats
				if (this.busy) return;
				this.busy = true;
				if (!regenerate) this.state = 'loading';

				try {
					const response = await this.createAppLoginSession({ regenerate });

					this.teardown();
					this.session = response.data;
					this.connected = !!response.data.connected;
					this.remaining = response.data.expiresIn;
					this.cooldown = regenerate ? REGENERATE_COOLDOWN : 0;
					this.state = 'ready';

					this.$websocket.$on('message', this.onMessage);
					this.interval = setInterval(this.tick, 1000);
				} catch (error) {
					// A refused regenerate leaves the running code untouched and still valid, so hold the
					// button rather than tearing down a session that is working perfectly well
					if (regenerate && error.status === 429) {
						this.cooldown = REGENERATE_COOLDOWN;
						if (!this.interval) this.interval = setInterval(this.tick, 1000);
					} else {
						this.teardown();
						this.state = 'error';
					}
				} finally {
					this.busy = false;
				}
			},

			/**
			 * @name tick
			 * @description Drive both countdowns off one timer, flipping to expired when the code runs
			 * out and stopping itself once there is nothing left to count
			 */
			tick() {
				if (this.cooldown > 0) this.cooldown -= 1;
				if (this.remaining > 0) this.remaining -= 1;

				if (this.remaining <= 0 && this.state === 'ready') {
					this.state = 'expired';
					this.connected = false;
					this.$websocket.$off('message', this.onMessage);
				}

				if (this.remaining <= 0 && this.cooldown <= 0) {
					clearInterval(this.interval);
					this.interval = null;
				}
			},

			/**
			 * @name onMessage
			 * @description The backend reports the device reaching the code screen, then completing
			 * @param {Object} message The websocket message
			 */
			onMessage(message) {
				if (message?.sessionId !== this.session.sessionId) return;

				if (message.type === 'appLoginConnected') {
					this.connected = true;
					return;
				}

				if (message.type !== 'appLoginCompleted') return;

				this.reset();
				this.state = 'success';
				this.timeout = setTimeout(this.close, 2500);
			},

			/**
			 * @name teardown
			 * @description Drop the timers and the socket listener, leaving what is on screen alone
			 */
			teardown() {
				if (this.interval) clearInterval(this.interval);
				if (this.timeout) clearTimeout(this.timeout);
				this.interval = null;
				this.timeout = null;
				this.$websocket.$off('message', this.onMessage);
			},

			/**
			 * @name reset
			 * @description Full stop, on close and on destroy, so nothing outlives the dialog. The
			 * session itself is deliberately left running in redis - reopening picks it back up
			 */
			reset() {
				this.teardown();
				this.cooldown = 0;
				this.connected = false;
			}
		}
	};
</script>

<style lang="scss" scoped>
	.app-login-code {
		font-size: 2.75rem;
		font-weight: 700;
		letter-spacing: 0.6rem;
		line-height: 1.2;
		// letter-spacing pads the right of the last glyph, this pulls the block back into centre
		text-indent: 0.6rem;
	}
</style>
